class EngravingPersonalizer extends HTMLElement {
  connectedCallback() {
    this.sides = ["front", "left", "back", "right"];
    this.zones = {
      front: {
        x: 96,
        y: 195,
        width: 58,
        height: 425,
        horizontalOffsetX: -4,
        verticalOffsetX: 0,
      },
      left: {
        x: 350,
        y: 235,
        width: 55,
        height: 385,
        horizontalOffsetX: -4,
        verticalOffsetX: 0,
      },
      back: {
        x: 601,
        y: 195,
        width: 58,
        height: 425,
        horizontalOffsetX: -6,
        verticalOffsetX: -6,
      },
      right: {
        x: 855,
        y: 235,
        width: 61,
        height: 385,
        horizontalOffsetX: 6,
        horizontalRunesOffsetX: 12,
        horizontalRunesMaximumSize: 68,
        verticalOffsetX: 6,
      },
    };
    this.alphabet = "English";
    this.dataset.alphabet = this.alphabet;
    this.svgNamespace = "http://www.w3.org/2000/svg";
    this.bindControls();
    this.mountPreview();
    this.bindMobileSheet();
    this.updateConfig();
    document.fonts?.ready.then(() => this.renderAll());
  }

  bindControls() {
    this.querySelectorAll("[data-engraving-alphabet]").forEach((input) => {
      input.addEventListener("change", () => {
        if (!input.checked) return;
        this.alphabet = input.value;
        this.dataset.alphabet = input.value;
        const hint = this.querySelector("[data-alphabet-hint]");
        if (hint) {
          hint.textContent =
            input.value === "Runes"
              ? "Type Latin letters to preview their matching runic forms."
              : "English uses a clear, engraving-friendly typeface.";
        }
        this.renderAll();
        this.updateConfig();
        this.syncMobileSheet();
      });
    });

    this.sides.forEach((side) => {
      const panel = this.querySelector(`[data-engraving-side="${side}"]`);
      const input = panel?.querySelector("[data-engraving-input]");
      const counter = panel?.querySelector("[data-engraving-counter]");
      if (!panel || !input || !counter) return;

      input.addEventListener("input", () => {
        const characters = Array.from(input.value).slice(0, 13);
        input.value = characters.join("");
        counter.textContent = `${characters.length}/13`;
        this.renderSide(side);
        this.updateConfig();
        this.syncMobileSheet(side);
      });

      input.addEventListener("focus", () => {
        this.selectMobileSide(side, { open: false });
        this.setActiveZone(side);
      });
      input.addEventListener("blur", () => this.setActiveZone(null));

      panel.querySelectorAll("[data-engraving-orientation]").forEach((orientation) => {
        orientation.addEventListener("change", () => {
          if (!orientation.checked) return;
          this.renderSide(side);
          this.updateConfig();
          this.syncMobileSheet(side);
        });
      });
    });
  }

  mountPreview() {
    const section = this.closest(".shopify-section") || document;
    const image =
      section.querySelector(
        `.product__media-image-wrapper img[src*="${this.dataset.targetImage}"]`
      ) ||
      Array.from(
        section.querySelectorAll(`img[src*="${this.dataset.targetImage}"]`)
      ).find((candidate) => !candidate.closest("product-sticky-form, .product-sticky-form")) ||
      section.querySelector(".product__media-image-wrapper img");
    if (!image) return;

    const wrapper = image.closest(".product__media-image-wrapper") || image.parentElement;
    if (!wrapper) return;

    wrapper.style.position = "relative";
    const svg = document.createElementNS(this.svgNamespace, "svg");
    svg.classList.add("engraving-preview-layer");
    Object.assign(svg.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
    });
    svg.setAttribute("viewBox", "0 0 1000 800");
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    svg.setAttribute("aria-hidden", "true");

    this.sides.forEach((side, index) => {
      const zone = this.zones[side];
      const hit = document.createElementNS(this.svgNamespace, "rect");
      hit.classList.add("engraving-preview-layer__hit");
      hit.dataset.previewHit = side;
      hit.setAttribute("x", index * 250);
      hit.setAttribute("y", 0);
      hit.setAttribute("width", 250);
      hit.setAttribute("height", 800);
      svg.append(hit);

      const rect = document.createElementNS(this.svgNamespace, "rect");
      rect.classList.add("engraving-preview-layer__zone");
      rect.dataset.previewZone = side;
      rect.setAttribute("x", zone.x);
      rect.setAttribute("y", zone.y);
      rect.setAttribute("width", zone.width);
      rect.setAttribute("height", zone.height);
      svg.append(rect);

      const text = document.createElementNS(this.svgNamespace, "text");
      text.classList.add("engraving-preview-layer__text");
      text.dataset.previewText = side;
      svg.append(text);
    });

    wrapper.append(svg);
    this.preview = svg;
    this.renderAll();
  }

  bindMobileSheet() {
    this.mobileSheet = this.querySelector("[data-engraving-mobile-sheet]");
    if (!this.mobileSheet) return;

    this.mobileSide = "front";
    this.mobileInput = this.querySelector("[data-engraving-mobile-input]");
    this.mobileCounter = this.querySelector("[data-engraving-mobile-counter]");
    this.mobileTitle = this.querySelector("[data-engraving-mobile-title]");
    this.mobileSummary = this.querySelector("[data-engraving-sheet-summary]");
    this.mobileHandle = this.querySelector("[data-engraving-sheet-handle]");

    this.querySelectorAll("[data-engraving-mobile-side]").forEach((button) => {
      button.addEventListener("click", () => {
        this.selectMobileSide(button.dataset.engravingMobileSide, { open: true });
      });
    });

    this.mobileInput?.addEventListener("input", () => {
      const mainInput = this.getSidePanel(this.mobileSide)?.querySelector("[data-engraving-input]");
      if (!mainInput) return;
      mainInput.value = Array.from(this.mobileInput.value).slice(0, 13).join("");
      mainInput.dispatchEvent(new Event("input", { bubbles: true }));
    });

    this.querySelectorAll("[data-engraving-mobile-alphabet]").forEach((input) => {
      input.addEventListener("change", () => {
        if (!input.checked) return;
        const mainInput = Array.from(
          this.querySelectorAll("[data-engraving-alphabet]")
        ).find((candidate) => candidate.value === input.value);
        if (!mainInput) return;
        mainInput.checked = true;
        mainInput.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    this.querySelectorAll("[data-engraving-mobile-orientation]").forEach((input) => {
      input.addEventListener("change", () => {
        if (!input.checked) return;
        const mainInput = Array.from(
          this.getSidePanel(this.mobileSide)?.querySelectorAll("[data-engraving-orientation]") || []
        ).find((candidate) => candidate.value === input.value);
        if (!mainInput) return;
        mainInput.checked = true;
        mainInput.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    this.querySelector("[data-engraving-sheet-done]")?.addEventListener("click", () => {
      this.setMobileSheetOpen(false);
    });

    this.mobileHandle?.addEventListener("click", () => {
      if (this.mobileSheetDragged) {
        this.mobileSheetDragged = false;
        return;
      }
      this.setMobileSheetOpen(!this.mobileSheet.classList.contains("is-open"));
    });

    this.bindMobileSheetDrag();

    this.preview?.querySelectorAll("[data-preview-hit]").forEach((hit) => {
      hit.addEventListener("pointerdown", (event) => {
        if (!window.matchMedia("(max-width: 740px)").matches) return;
        event.preventDefault();
        event.stopPropagation();
      });
      hit.addEventListener("click", (event) => {
        if (!window.matchMedia("(max-width: 740px)").matches) return;
        event.preventDefault();
        event.stopPropagation();
        this.selectMobileSide(hit.dataset.previewHit, { open: true, focus: true });
      });
    });

    this.selectMobileSide("front", { open: false });
  }

  bindMobileSheetDrag() {
    if (!this.mobileHandle || !this.mobileSheet) return;

    this.mobileHandle.addEventListener("pointerdown", (event) => {
      this.mobileSheetDragged = false;
      this.mobileSheetStartY = event.clientY;
      const transform = getComputedStyle(this.mobileSheet).transform;
      this.mobileSheetStartOffset =
        transform === "none" ? 0 : new DOMMatrixReadOnly(transform).m42;
      this.mobileSheet.classList.add("is-dragging");
      this.mobileHandle.setPointerCapture(event.pointerId);
    });

    this.mobileHandle.addEventListener("pointermove", (event) => {
      if (!this.mobileHandle.hasPointerCapture(event.pointerId)) return;
      const delta = event.clientY - this.mobileSheetStartY;
      if (Math.abs(delta) > 6) this.mobileSheetDragged = true;
      const offset = Math.max(0, this.mobileSheetStartOffset + delta);
      this.mobileSheet.style.transform = `translateY(${offset}px)`;
    });

    const finishDrag = (event) => {
      if (!this.mobileHandle.hasPointerCapture(event.pointerId)) return;
      this.mobileHandle.releasePointerCapture(event.pointerId);
      const delta = event.clientY - this.mobileSheetStartY;
      if (delta > 70) this.setMobileSheetOpen(false);
      if (delta < -55) this.setMobileSheetOpen(true);
      this.mobileSheet.classList.remove("is-dragging");
      requestAnimationFrame(() => this.mobileSheet.style.removeProperty("transform"));
    };

    this.mobileHandle.addEventListener("pointerup", finishDrag);
    this.mobileHandle.addEventListener("pointercancel", finishDrag);
  }

  getSidePanel(side) {
    return this.querySelector(`[data-engraving-side="${side}"]`);
  }

  selectMobileSide(side, options = {}) {
    if (!this.sides.includes(side) || !this.mobileSheet) return;
    this.mobileSide = side;
    this.mobileSheet.dataset.selectedSide = side;
    this.querySelectorAll("[data-engraving-mobile-side]").forEach((button) => {
      button.setAttribute(
        "aria-pressed",
        button.dataset.engravingMobileSide === side ? "true" : "false"
      );
    });
    this.syncMobileSheet(side, { forceValue: true });
    this.setActiveZone(side);
    if (options.open) this.setMobileSheetOpen(true);
    if (options.focus) {
      window.setTimeout(() => this.mobileInput?.focus({ preventScroll: true }), 340);
    }
  }

  syncMobileSheet(side = this.mobileSide, options = {}) {
    if (!this.mobileSheet || side !== this.mobileSide) return;
    const state = this.getSideState(side);
    const mainInput = this.getSidePanel(side)?.querySelector("[data-engraving-input]");
    const sideLabel = `${side.charAt(0).toUpperCase()}${side.slice(1)}`;

    if (
      this.mobileInput &&
      (options.forceValue || this.mobileInput !== document.activeElement)
    ) {
      this.mobileInput.value = mainInput?.value || "";
    }
    if (this.mobileCounter) {
      this.mobileCounter.textContent = `${Array.from(mainInput?.value || "").length}/13`;
    }
    if (this.mobileTitle) this.mobileTitle.textContent = sideLabel;
    if (this.mobileSummary) this.mobileSummary.textContent = `Edit ${sideLabel} engraving`;

    this.querySelectorAll("[data-engraving-mobile-alphabet]").forEach((input) => {
      input.checked = input.value === this.alphabet;
    });
    this.querySelectorAll("[data-engraving-mobile-orientation]").forEach((input) => {
      input.checked = input.value === state.orientation;
    });
  }

  setMobileSheetOpen(open) {
    if (!this.mobileSheet || !this.mobileHandle) return;
    this.mobileSheet.classList.toggle("is-open", open);
    this.mobileHandle.setAttribute("aria-expanded", open ? "true" : "false");
    if (!open) {
      this.mobileInput?.blur();
      this.setActiveZone(null);
    } else {
      this.setActiveZone(this.mobileSide);
    }
  }

  getSideState(side) {
    const panel = this.querySelector(`[data-engraving-side="${side}"]`);
    const input = panel?.querySelector("[data-engraving-input]");
    const orientation = panel?.querySelector("[data-engraving-orientation]:checked");
    return {
      text: input?.value.trim() || "",
      orientation: orientation?.value || "Horizontal",
    };
  }

  renderAll() {
    this.sides.forEach((side) => this.renderSide(side));
  }

  renderSide(side) {
    if (!this.preview) return;
    const element = this.preview.querySelector(`[data-preview-text="${side}"]`);
    const zone = this.zones[side];
    const state = this.getSideState(side);
    if (!element || !zone) return;

    element.replaceChildren();
    element.removeAttribute("transform");
    element.classList.toggle("is-runes", this.alphabet === "Runes");
    element.classList.toggle("is-english", this.alphabet !== "Runes");
    element.setAttribute("x", zone.x + zone.width / 2);
    element.setAttribute("y", zone.y + zone.height / 2);

    if (!state.text) return;

    if (state.orientation === "Vertical") {
      this.renderVertical(element, state.text, zone);
    } else {
      this.renderHorizontal(element, state.text, zone);
    }
  }

  renderHorizontal(element, value, zone) {
    const isRunes = this.alphabet === "Runes";
    const characterCount = Array.from(value).length;
    const horizontalOffset =
      isRunes && Number.isFinite(zone.horizontalRunesOffsetX)
        ? zone.horizontalRunesOffsetX
        : zone.horizontalOffsetX || 0;
    const centerX = zone.x + zone.width / 2 + horizontalOffset;
    const centerY = zone.y + zone.height / 2;
    element.textContent = value;
    element.setAttribute("x", centerX);
    element.setAttribute("y", centerY);
    element.setAttribute("dominant-baseline", "middle");
    element.setAttribute("transform", `rotate(90 ${centerX} ${centerY})`);

    const runesMaximumSize = zone.horizontalRunesMaximumSize || 78;
    const maximumSize = Math.min(
      isRunes ? runesMaximumSize : 46,
      zone.width * (isRunes ? 1.32 : 0.72)
    );
    let size = maximumSize;
    let spacing = characterCount > 1 ? size * (isRunes ? 0.2 : 0.03) : 0;
    element.setAttribute("font-size", size);
    element.setAttribute("letter-spacing", spacing);

    const maxLength = zone.height * (isRunes ? 0.74 : 0.86);
    const measured = Math.max(
      element.getComputedTextLength(),
      element.getBBox().width
    );
    if (measured > maxLength && measured > 0) {
      size = Math.max(isRunes ? 14 : 18, size * (maxLength / measured));
      spacing = characterCount > 1 ? size * (isRunes ? 0.2 : 0.03) : 0;
      element.setAttribute("font-size", size);
      element.setAttribute("letter-spacing", spacing);

      const fittedMeasurement = Math.max(
        element.getComputedTextLength(),
        element.getBBox().width
      );
      if (fittedMeasurement > maxLength && fittedMeasurement > 0) {
        size = Math.max(
          isRunes ? 14 : 18,
          size * (maxLength / fittedMeasurement)
        );
        spacing = characterCount > 1 ? size * (isRunes ? 0.2 : 0.03) : 0;
        element.setAttribute("font-size", size);
        element.setAttribute("letter-spacing", spacing);
      }
    }
  }

  renderVertical(element, value, zone) {
    const characters = Array.from(value);
    const isRunes = this.alphabet === "Runes";
    const centerX = zone.x + zone.width / 2 + (zone.verticalOffsetX || 0);
    const availableHeight = zone.height * 0.86;
    const lineHeightRatio = isRunes ? 1.2 : 1.08;
    const maximumSize = Math.min(isRunes ? 54 : 46, zone.width * (isRunes ? 0.9 : 0.72));
    const requiredLines =
      characters.length > 1 ? 1 + (characters.length - 1) * lineHeightRatio : 1;
    const fontSize = Math.min(
      maximumSize,
      availableHeight / requiredLines
    );
    const lineHeight = fontSize * lineHeightRatio;
    const totalHeight = lineHeight * Math.max(characters.length - 1, 0);
    const startY = zone.y + zone.height / 2 - totalHeight / 2;

    element.setAttribute("font-size", Math.max(fontSize, isRunes ? 19 : 17));
    element.removeAttribute("letter-spacing");
    element.removeAttribute("dominant-baseline");
    characters.forEach((character, index) => {
      const tspan = document.createElementNS(this.svgNamespace, "tspan");
      tspan.setAttribute("x", centerX);
      tspan.setAttribute("y", startY + index * lineHeight);
      tspan.setAttribute("dominant-baseline", "middle");
      tspan.textContent = character;
      element.append(tspan);
    });
  }

  setActiveZone(side) {
    if (!this.preview) return;
    this.preview.querySelectorAll("[data-preview-zone]").forEach((zone) => {
      zone.classList.toggle("is-active", zone.dataset.previewZone === side);
    });
  }

  updateConfig() {
    const config = this.querySelector("[data-engraving-config]");
    if (!config) return;
    const sides = {};
    this.sides.forEach((side) => {
      const state = this.getSideState(side);
      sides[side] = {
        text: state.text,
        orientation: state.orientation,
      };
    });
    config.value = JSON.stringify({
      alphabet: this.alphabet,
      sides,
    });
  }
}

if (!customElements.get("engraving-personalizer")) {
  customElements.define("engraving-personalizer", EngravingPersonalizer);
}
